import React, { useEffect, useState, useRef } from "react";

//routing
import { useParams, useNavigate } from "react-router-dom";

//schemas
import form from "./schema/form";
import validation from "./schema/validation";
import initialValues from "./schema/initialValues";

//external components
import { Box, Card, Grid, CircularProgress } from "@mui/material";

//components
import IALoadingButton from "../../../../components/IAButtons/IALoadingButton";

//pages
import Main from "./components/main";
import SideInfo from "./components/sideInfo";

// formik components
import { Formik, Form } from "formik";

//translation
import { useTranslation } from "react-i18next";

//hooks
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";
import { useSnackbar } from "notistack";
import useAuth from "../../../../hooks/useAuth";

//graphql
import {
  CREATE_ROLE,
  UPDATE_ROLE,
  GET_ROLE_BY_ID,
} from "../../../../graphql/role";

//urls
import urls from "../../../../urls";

import ContainerCard from "../../../../components/Cards/ContainerCard";

import Help from "./components/_help";

const ROLE_VALUES = [
  {
    text: "roleForm.role.value.manage",
    code: "M",
  },
  { text: "roleForm.role.value.read", code: "R" },
  { text: "roleForm.role.value.noaccess", code: "N" },
];

const RoleForm = ({
  fromModal = false,
  handleCloseModal,
  resetFields = false,
}) => {
  const { tName, tID, rName, rID } = useParams();

  const createMode = !rName && !rID;

  const { storeUserTenant, userRoles } = useAuth();

  const { manageParameters: manageParametersRole } = userRoles;

  const { tenant_list, tenant_roles, my_roles_list } = urls;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { formField } = form;

  const [sideInfoData, setSideInfoData] = useState({});

  const [isSystemGenerated, setIsSystemGenerated] = useState(false);

  const {
    name,
    description,
    manageSystemConfigurations,
    manageTimeEntry,
    manageUsers,
    manageAttendance,
    manageParameters,
  } = formField;

  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState(initialValues);

  const [whatField, setWhatField] = useState(null);

  const [unTouchedForm, setUnTouchedForm] = useState(true);

  const formikValuesRef = useRef(null);
  const formikRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl + S
      if (event.key === 's' && event.ctrlKey) {
        event.preventDefault(); // Prevent the default behavior
        formikRef.current?.handleSubmit(formikValuesRef.current); // Submit the form
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
  
    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (resetFields === true) {
      let f = document.getElementById(form.formID);
      f?.reset();
    }
  }, [resetFields]);

  const { loading: getDataByIDLoading } = useQueryAuth(
    GET_ROLE_BY_ID,
    "GetRole",
    {
      variables: { id: rID },
      skip: createMode,
    },
    {
      onCompleted: (response) => {
        const data = response?.GetRole?.role?.roleObject || {};

        setFormData({
          [name.name]: data.name || "",
          [description.name]: data.description || "",
          [manageSystemConfigurations.name]:
            ROLE_VALUES.find(
              (x) => x.code === data.manageSystemConfigurations
            ) || null,
          [manageTimeEntry.name]:
            ROLE_VALUES.find((x) => x.code === data.manageTimeEntry) || null,
          [manageAttendance.name]:
            ROLE_VALUES.find((x) => x.code === data.manageAttendance) || null,
          [manageUsers.name]:
            ROLE_VALUES.find((x) => x.code === data.manageUsers) || null,
          [manageParameters.name]:
            ROLE_VALUES.find((x) => x.code === data.manageParameters) || null,
        });

        setIsSystemGenerated(data.isSystemGenerated || false);
      },
    }
  );

  const { cfunction: editRoleData } = useMutationAuth(UPDATE_ROLE, "EditRole");

  const { cfunction: createRoleData } = useMutationAuth(
    CREATE_ROLE,
    "CreateRole"
  );

  function createTenant(values, actions) {
    createRoleData(
      {
        variables: {
          model: {
            tenantGuid: tID ? tID : storeUserTenant,
            description: values[description.name],
            manageSystemConfigurations:
              values[manageSystemConfigurations.name].code,
            manageTimeEntry: values[manageTimeEntry.name].code,
            manageAttendance: values[manageAttendance.name].code,
            manageUsers: values[manageUsers.name].code,
            name: values[name.name],
            manageParameters: values[manageParameters.name].code,
          },
        },
      },
      (response) => {
        if (
          response["CreateRole"].errorCode !== null &&
          response["CreateRole"].errorCode !== ""
        ) {
          enqueueSnackbar(t(response["CreateRole"].errorMessage), {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(t("roleForm.createdSuccessfully"), {
            variant: "success",
          });
          sessionStorage.removeItem("unsavedChanges");
          if (fromModal === true && handleCloseModal !== undefined)
            handleCloseModal(false);
          else
            navigate(
              tID
                ? tenant_roles.replace(
                    "/:tName/:tID",
                    `/${encodeURIComponent(tName)}/${encodeURIComponent(tID)}`
                  )
                : my_roles_list
            );
        }
        actions.setSubmitting(false);
      },
      (error) => {
        actions.setSubmitting(false);
      }
    );
  }

  function editTenant(values, actions) {
    editRoleData(
      {
        variables: {
          model: {
            roleID: rID,
            //tenantGuid: myRoles? storeUserTenant :tID,
            description: values[description.name],
            manageSystemConfigurations:
              values[manageSystemConfigurations.name].code,
            manageTimeEntry: values[manageTimeEntry.name].code,
            manageAttendance: values[manageAttendance.name].code,
            manageUsers: values[manageUsers.name].code,
            name: values[name.name],
            manageParameters: values[manageParameters.name].code,
          },
        },
      },
      (response) => {
        if (
          response["EditRole"].errorCode !== null &&
          response["EditRole"].errorCode !== ""
        ) {
          enqueueSnackbar(t(response["EditRole"].errorMessage), {
            variant: "error",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar(t("roleForm.updatedSuccessfully"), {
            variant: "success",
          });
          sessionStorage.removeItem("unsavedChanges");
        }
        actions.setSubmitting(false);
      },
      (error) => {
        actions.setSubmitting(false);
      }
    );
  }

  useEffect(() => {
    if (
      performance.navigation.type === performance.navigation.TYPE_NAVIGATE &&
      unTouchedForm && !fromModal
    ) {
      sessionStorage.clear();
    }
  }, []);

  const handleSubmit = (values, actions) => {
    if (createMode) {
      createTenant(values, actions);
    } else {
      editTenant(values, actions);
    }
  };

  return (
    <>
      {getDataByIDLoading ? (
        <ContainerCard sx={{ height: "50vh" }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
          >
            <CircularProgress />
          </Box>
        </ContainerCard>
      ) : (
        <Box mb={5}>
          <Grid
            container
            justifyContent="left"
            alignItems="stretch"
            sx={{ height: "100%", mt: 2 }}
            spacing={2}
          >
            <Grid item xs={12} sm={12} md={12} lg={createMode ? 12 : 9}>
              <ContainerCard>
                <Formik
                  innerRef={formikRef}
                  key={`${form.formID}-form-${
                    createMode ? "creation" : "edit"
                  }`}
                  initialValues={formData}
                  validationSchema={validation[0]}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    values,
                    errors,
                    touched,
                    isSubmitting,
                    setFieldValue,
                    setTouched,
                  }) =>  {
                    formikValuesRef.current = values;
                    return (
                    <Form
                      id={form.formID}
                      autoComplete="off"
                      onChange={() => {
                        if (touched && fromModal !== true) {
                          sessionStorage.setItem(
                            "unsavedChanges",
                            JSON.stringify(true)
                          );
                          setUnTouchedForm(false);
                        }
                      }}
                    >
                      <Box p={2}>
                        <Box>
                          <Main
                            formData={{
                              values,
                              touched,
                              formField: form.formField,
                              errors,
                              setFieldValue,
                              setTouched,
                              createMode,
                              whatField,
                              setWhatField,
                              readOnly:
                                manageParametersRole === "R" ||
                                isSystemGenerated === true,
                            }}
                          />
                        </Box>
                        <Box
                          mt={2}
                          width="100%"
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <IALoadingButton
                            disabled={
                              isSubmitting || manageParametersRole !== "M"
                            }
                            loading={isSubmitting}
                            type="submit"
                            label={t("roleForm.save")}
                          />
                        </Box>
                      </Box>
                    </Form>
                  )}}
                </Formik>
              </ContainerCard>
            </Grid>
            {whatField && (
              <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                <ContainerCard>
                  <Box p={2}>
                    <Help data={whatField} title={"roleForm.whatField"} />
                  </Box>
                </ContainerCard>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
};

export default RoleForm;
